import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { loginByPhone, getUserInfo } from '@/api/modules/account'
import { request } from '@/api/request'
import router from '@/router'
import { useMenuStore } from '@/stores/menu'

export const useUserStore = defineStore('user', () => {
  const loginData = ref(null)
  const rememberAccount = ref(localStorage.getItem('account_phone') || '')
  const pwdSecurity = ref({
    checked: false,
    weak: false,
    forced: false,
  })

  // 计算属性
  const isLoggedIn = computed(() => !!loginData.value && !!loginData.value.token)
  const token = computed(() => loginData.value?.token || '')
  const userName = computed(() => loginData.value?.user_name || loginData.value?.account || '')
  const userRole = computed(() => loginData.value?.role || 0)
  const userId = computed(() => loginData.value?.user_id || 0)

  /** 从 localStorage 恢复登录状态 */
  function loadFromStorage() {
    const stored = localStorage.getItem('login_data')
    if (stored) {
      try {
        loginData.value = JSON.parse(stored)
      } catch {
        loginData.value = null
      }
    }
  }

  /** 登录 */
  async function login(params) {
    const data = await loginByPhone({
      account: params.account_phone,
      password: params.password,
      code_id: params.code_id,
      code: params.code,
    })

    // 保存记住账号的值
    const accountToRemember = params.rememberAccount ? params.account_phone : null

    // 登录前清空浏览器缓存（但先保存需要记住的账号）
    localStorage.clear()

    // 重置菜单 store（清空旧账号的菜单数据，让新账号重新加载）
    const menuStore = useMenuStore()
    menuStore.reset()

    // 记住账号
    if (accountToRemember) {
      localStorage.setItem('account_phone', accountToRemember)
      rememberAccount.value = accountToRemember
    }

    // 缓存登录用户数据
    loginData.value = data
    localStorage.setItem('login_data', JSON.stringify(data))

    // 登录成功后异步检查密码安全（不阻塞登录流程）
    checkPwdSecurity()

    return data
  }

  /** 退出登录 */
  function logout() {
    loginData.value = null
    localStorage.clear()
    pwdSecurity.value = { checked: false, weak: false, forced: false }
    // 重置菜单 store，避免旧账号的菜单数据被复用
    const menuStore = useMenuStore()
    menuStore.reset()
    router.replace('/login')
  }

  /** 刷新 Token：调用后端 refreshToken 接口获取新 token 并更新缓存 */
  async function refreshToken() {
    if (!isLoggedIn.value) return
    try {
      const data = await request('account/refreshToken', { _hideLoading: true })
      if (data && data.token) {
        loginData.value.token = data.token
        localStorage.setItem('login_data', JSON.stringify(loginData.value))
      }
    } catch {
      // token 已失效，执行退出登录
      logout()
    }
  }

  /** 检查密码强度（与后端规则保持一致） */
  function isWeakPassword(password) {
    if (!password) return true
    if (password.length < 8) return true
    if (!/[A-Z]/.test(password)) return true
    if (!/[a-z]/.test(password)) return true
    if (!/\d/.test(password)) return true
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) return true
    return false
  }

  /** 检查密码安全状态：仅检测弱密码，弱密码立即强制修改 */
  async function checkPwdSecurity() {
    if (!isLoggedIn.value || !userId.value) return
    try {
      const info = await getUserInfo({ user_id: userId.value, _hideLoading: true })
      const password = info?.password || ''
      const weak = isWeakPassword(password)

      if (weak) {
        pwdSecurity.value = { checked: true, weak: true, forced: true }
        if (router.currentRoute.value.path !== '/change-password') {
          router.push('/change-password')
        }
      } else {
        pwdSecurity.value = { checked: true, weak: false, forced: false }
      }
    } catch (err) {
      console.error('密码安全检查失败:', err)
    }
  }

  /** 检查用户是否有指定角色权限 */
  function hasRole(roleList) {
    if (!roleList || roleList.length === 0) return true
    return roleList.includes(userRole.value)
  }

  return {
    loginData,
    rememberAccount,
    isLoggedIn,
    token,
    userName,
    userRole,
    userId,
    pwdSecurity,
    loadFromStorage,
    login,
    logout,
    refreshToken,
    checkPwdSecurity,
    hasRole,
  }
})
